import { makeObservable, override } from 'mobx';
import escape from 'lodash/escape';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';

import { DictionaryNames, DrugStatus, EntryTypeFieldType, MedicationStatus } from 'chaika-enums';

import { translate } from '@chaika/i18n/helper';
import { makeDrugsData } from '@patient/ui/SoapEntryPage/components/Template/SystemBlock/Medications/utils/makeDrugsData';

import { store } from '@store';
import { AbstractListField, ListValueField } from '@store/models/EntryFields/Field/AbstractListField';
import { getVisibleUsedMedications, MedicationList, MedicationParams } from '@store/models/SystemFields/Medications';

export const NOT_USED_MEDICINE_ITEM: MedicationParams = {
    id: 'a71033a8-caa8-4122-93a6-871d9ce205c4',
    activeSubstanceId: '',
    frequencyIfReception: '',
    customTradeNames: [],
    start: new Date().toISOString(),
    status: MedicationStatus.Active,
    isDefault: true,
    authorId: '',
    parentEntryId: '',
};

export class MedicationField extends AbstractListField<MedicationList, MedicationParams> {
    constructor(props: MedicationList | MedicationParams[], name: string) {
        super(props, name);
        makeObservable(this);
    }

    public get hasHtmlPreview(): boolean {
        return true;
    }

    @override
    public get hasData(): boolean {
        return [...this.medications, ...this.ehrMedications].length > 0;
    }

    public get hasActiveData(): boolean {
        return [...this.allActiveMedication].length > 0;
    }

    public get allActiveMedication(): MedicationParams[] {
        return uniqBy([...this.medications, ...this.ehrMedications], 'id').filter(
            it => it.status === MedicationStatus.Active,
        );
    }

    public isEqual(value: MedicationList): boolean {
        const medicationParams = this.makeUpdateParams();

        return isEqual(medicationParams, value.serialize());
    }

    public getPreview(): string {
        return getVisibleUsedMedications([...this.allActiveMedication, ...this.episodesDrugs])
            .map(medication => this.getMedicationRevision(medication))
            .join('\n');
    }

    public getMedicationRevision(medication: MedicationParams, withoutStrong = false): string {
        if (!!medication?.isDefault) {
            const dontTakeText = translate('dontTake');

            return withoutStrong ? dontTakeText : `<strong>${dontTakeText}</strong>`;
        }

        const { title, tradeNames } = this.getMedicationPreviewParams(medication);

        return withoutStrong ? `${tradeNames} - ${title}` : `<strong>${escape(tradeNames)} -</strong> ${escape(title)}`;
    }

    public getMedicationPreviewParams(medication: MedicationParams): { title: string; tradeNames: string } {
        const { dictionary } = store;

        const medicationTradeNamesDictionary = dictionary.getDictionary(DictionaryNames.DrugTradeNames);
        const activeDictionary = dictionary.getDictionary(DictionaryNames.ElementDrug);

        const customTradeNames = medication.customTradeNames ?? [];
        const tradeNames = [
            ...(medication.tradeNameIds ?? []).map(id => {
                const reaction = medicationTradeNamesDictionary.get(id);

                return reaction?.title ?? '-';
            }),
            ...customTradeNames,
        ].join(', ');

        return {
            tradeNames,
            title: activeDictionary.get(medication.activeSubstanceId)?.title ?? medication.frequencyIfReception ?? '-',
        };
    }

    public makeUpdateParams(): MedicationParams[] {
        return this.fieldValue.serialize();
    }

    protected setFieldValue(value: ListValueField<MedicationParams>): void {
        const formattedValues = this.makeValueArray(value);

        this.fieldValue = new MedicationList(formattedValues);
    }

    private get ehrMedications(): MedicationParams[] {
        const currentIds = new Set<string>(this.fieldValue.serialize().map(medication => medication.id));
        return store.entry.ehrFields
            .get(EntryTypeFieldType.UsedMedications)
            .getValue()
            .serialize()
            .filter((it: MedicationParams) => it.status !== MedicationStatus.Deleted && !currentIds.has(it.id));
    }

    private get medications(): MedicationParams[] {
        return this.fieldValue.serialize().filter(it => it.status !== MedicationStatus.Deleted);
    }

    private get episodesDrugs(): MedicationParams[] {
        const { otherDrugs, activeEhrDrugs } = makeDrugsData();
        const drugs = new MedicationList(
            [...otherDrugs, ...activeEhrDrugs].map(({ activeSubstance, status, ...drug }) => ({
                ...drug,
                status: status === DrugStatus.Active ? MedicationStatus.Active : MedicationStatus.Deleted,
                activeSubstanceId: activeSubstance,
            })),
        );
        return drugs.values;
    }
}
